import {
	PERSON_SEARCH_TITLE_MAX_COUNT,
	PERSON_SEARCH_TITLE_MAX_LENGTH,
	personSearchTitlePreview,
} from "./person-search-title";

/**
 * The notes a finished list run leaves in `list.processingStatusMessage` for the user (PREC-4470).
 *
 * The same column also holds progress labels ("5/5 Add to list", "0/0"), and many finished lists
 * keep their last one, so the web cannot show whatever a DONE list carries. It shows a DONE list's
 * message only when it is one of these notes, and the API writes them through this module, so the
 * wording and the recogniser cannot drift apart.
 */

/** A delivered file waits for its column mapping (PREC-3922); the API's AWAITING_FILE_MAPPING. */
export const FILE_AWAITING_MAPPING_NOTE = "File awaiting column mapping";

const UNRESOLVED_COMPANIES_SUFFIX = " could not be researched — re-run the list to try them again";

/** "3 companies could not be researched — re-run the list to try them again". */
export function unresolvedCompaniesNote(count: number): string {
	const companies = count === 1 ? "1 company" : `${count} companies`;

	return `${companies}${UNRESOLVED_COMPANIES_SUFFIX}`;
}

const UNSEARCHED_PEOPLE_SUFFIX = " could not be searched — re-run the list to try them again";

const UNSEARCHED_PEOPLE_AT_LIMIT_SUFFIX =
	" could not be searched because the list reached its prospect limit — raise the limit and re-run the list to search them";

function people(count: number): string {
	return count === 1 ? "1 person" : `${count} people`;
}

/**
 * "17 people could not be searched — re-run the list to try them again" (PREC-4606): uploaded
 * people a run left unsettled, which a later run searches again. The run used to finish DONE
 * saying only that companies were unresolved.
 */
export function unsearchedPeopleNote(count: number): string {
	return `${people(count)}${UNSEARCHED_PEOPLE_SUFFIX}`;
}

/**
 * "3 people could not be searched because the list reached its prospect limit — raise the limit
 * and re-run the list to search them" (PREC-4606): said instead of {@link unsearchedPeopleNote}
 * when the rows a previous run enriched already used up the list's lead limit. Re-running the
 * list unchanged keeps the same limit (`setMaxProspects`), so it would leave these rows again.
 */
export function unsearchedPeopleAtLimitNote(count: number): string {
	return `${people(count)}${UNSEARCHED_PEOPLE_AT_LIMIT_SUFFIX}`;
}

/**
 * "67 companies could not be researched — the persona "HR Department" has a job title over 250
 * characters ("Recruiter Senior Recruiter Technical Rec…"); shorten it in Lead Scoring, then re-run
 * the list" (PREC-4987). Said instead of {@link unresolvedCompaniesNote} when a search profile of
 * the list holds a title the people search refuses: re-running the list unchanged meets the same
 * refusal, so the note names what to fix first. `personaName` is null for a profile that searches
 * with its own titles rather than a persona's.
 */
export function invalidSearchTitleNote(
	count: number,
	personaName: string | null,
	title: string
): string {
	const companies = count === 1 ? "1 company" : `${count} companies`;
	const holder =
		personaName === null ? "a search profile of this list" : `the persona "${personaName}"`;
	const where = personaName === null ? "in the list's search profile" : "in Lead Scoring";

	return `${companies} could not be researched — ${holder} has a job title over ${PERSON_SEARCH_TITLE_MAX_LENGTH} characters ("${personSearchTitlePreview(title)}"); shorten it ${where}, then re-run the list`;
}

/** A count refusal keeps the finished-note prefix, so existing readers display it too. */
export function tooManySearchTitlesNote(
	count: number,
	personaName: string | null,
	titleCount: number
): string {
	const companies = count === 1 ? "1 company" : `${count} companies`;
	const holder =
		personaName === null ? "a search profile of this list" : `the persona "${personaName}"`;
	const where = personaName === null ? "in the list's search profile" : "in Lead Scoring";

	return `${companies} could not be researched — ${holder} has ${titleCount} job titles (at most ${PERSON_SEARCH_TITLE_MAX_COUNT} are allowed); remove titles until ${PERSON_SEARCH_TITLE_MAX_COUNT} or fewer remain ${where}, then re-run the list`;
}

/** How many characters of a Maps query the note quotes before the ellipsis (PREC-4630). */
export const GOOGLE_MAPS_QUERY_PREVIEW_LENGTH = 80;

const GOOGLE_MAPS_SEARCH_SUFFIX = " could not be run — re-run the list to try ";

function mapsQueryPreview(query: string): string {
	// The note is one line of a status column, so a line break in the query would split it and the
	// recogniser below would no longer see a whole note (PREC-4630 review). Any run of whitespace
	// becomes a single space, and the ends are trimmed; an empty result means there is nothing to
	// quote.
	const normalized = query.replace(/\s+/gu, " ").trim();
	const characters = Array.from(normalized);

	return characters.length > GOOGLE_MAPS_QUERY_PREVIEW_LENGTH
		? `${characters.slice(0, GOOGLE_MAPS_QUERY_PREVIEW_LENGTH).join("")}…`
		: normalized;
}

/**
 * "1 Google Maps search could not be run — re-run the list to try it again" (PREC-4630), or the
 * same with a count: the short form the run-end note falls back to when quoting a query would
 * push the joined message past the status column. Null for no failed search.
 */
export function failedMapsCountNote(count: number): string | null {
	if (count <= 0) {
		return null;
	}

	const searches = count === 1 ? "1 Google Maps search" : `${count} Google Maps searches`;

	return `${searches}${GOOGLE_MAPS_SEARCH_SUFFIX}${count === 1 ? "it" : "them"} again`;
}

/**
 * "The Google Maps search for "cafés in Berlin" could not be run — re-run the list to try it
 * again" (PREC-4630): the Maps sources a run ended with in ERROR, which Re-Run searches again.
 * The list used to end DONE showing only the other sources' leads, and the failed search was
 * never mentioned. Several searches are said by their count, so the note stays short; one quotes
 * its query, bounded like the over-long-title note (PREC-4987). Null when no search failed, so a
 * list without one keeps its message unchanged.
 */
export function failedMapsNote(queries: readonly string[]): string | null {
	if (queries.length === 0) {
		return null;
	}

	if (queries.length === 1) {
		const preview = mapsQueryPreview(queries[0]);

		// A query that is empty or whitespace only has nothing to quote; the count form still names
		// the failure, so the note stays visible (PREC-4630 review). An empty quoted form ("") is
		// exactly what the recogniser cannot tell from a progress label.
		if (preview.length > 0) {
			return `The Google Maps search for "${preview}"${GOOGLE_MAPS_SEARCH_SUFFIX}it again`;
		}
	}

	return failedMapsCountNote(queries.length);
}

const FILE_IMPORT_FAILED_SUFFIX =
	" could not be imported — run the list again, or contact support if it keeps failing";

/**
 * "1 file could not be imported — run the list again, or contact support if it keeps failing".
 * A mapped file whose import failed on this run (PREC-4483): it is left un-imported, so the next
 * run tries it again. It used to end as a plain DONE with no message and no leads.
 */
export function fileImportFailedNote(count: number): string {
	const files = count === 1 ? "1 file" : `${count} files`;

	return `${files}${FILE_IMPORT_FAILED_SUFFIX}`;
}

/**
 * Several notes as one message, in the order given, leaving out the ones that are not there; null
 * when none is. A file that could not be imported and companies that could not be researched are
 * both said (PREC-4483): the file's note comes first, which is what `isFinishedRunNote` recognises.
 */
export function joinRunEndNotes(notes: (string | null | undefined)[]): string | null {
	const present = notes.filter((note): note is string => !!note);

	return present.length > 0 ? present.join(". ") : null;
}

/** Whether a finished (DONE) list's message is a note for the user rather than a stale label. */
export function isFinishedRunNote(message: string | null | undefined): boolean {
	if (!message) {
		return false;
	}

	return (
		message === FILE_AWAITING_MAPPING_NOTE ||
		// The awaiting-mapping note says why on its own, but a failed Maps search is joined after it.
		message.startsWith(`${FILE_AWAITING_MAPPING_NOTE}. `) ||
		/^\d+ compan(?:y|ies) could not be researched — /.test(message) ||
		/^\d+ files? could not be imported — /.test(message) ||
		/^\d+ (?:person|people) could not be searched — /.test(message) ||
		/^\d+ (?:person|people) could not be searched because the list reached its prospect limit — /.test(
			message
		) ||
		/^The Google Maps search for ".+" could not be run — /.test(message) ||
		/^\d+ Google Maps search(?:es)? could not be run — /.test(message)
	);
}
